import { Link } from 'react-router-dom';
import { businessInfo, navigationLinks } from '../data';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="border-t border-brand-sage-medium/20 bg-brand-cream/40 text-brand-neutral-dark">
      <div className="mx-auto max-w-7xl px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
          
          {/* Column 1: Brand & RAB */}
          <div className="sm:col-span-2 md:col-span-3 lg:col-span-2 flex flex-col gap-5">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-sage text-white">
                <i className="fa-light fa-hands-holding text-base" />
              </div>
              <span className="font-serif text-lg font-semibold tracking-wide">
                Stausbroe Bowen Terapi
              </span>
            </Link>
            <p className="max-w-md text-sm leading-relaxed text-brand-neutral-dark/80">
              Nænsom og dybdegående kropsterapi i Broby på Fyn. Vi arbejder med kroppens eget tempo og naturlige balance for at skabe ro, nærvær og tryghed.
            </p>
            
            {/* RAB Badge */}
            <div className="mt-2 flex items-center gap-4 rounded-xl border border-brand-sage-medium/20 bg-white/60 p-4 max-w-sm">
              {/* Custom SVG RAB Badge */}
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-sage-light text-brand-sage" aria-hidden="true">
                <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
                </svg>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-brand-sage-dark">RAB Registreret</p>
                <p className="text-xs text-brand-neutral-dark/80 mt-0.5">
                  Medlem af{' '}
                  <a 
                    href={businessInfo.rabLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="underline hover:text-brand-sage transition-colors duration-300"
                  >
                    {businessInfo.rabAssociation}
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div className="flex flex-col gap-4">
            <h3 className="font-serif text-base font-semibold tracking-wide">Hurtige links</h3>
            <ul className="flex flex-col gap-2.5 text-sm">
              {navigationLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="text-brand-neutral-dark/80 hover:text-brand-sage transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Contact & Info */}
          <div className="flex flex-col gap-4">
            <h3 className="font-serif text-base font-semibold tracking-wide">Kontakt</h3>
            <ul className="flex flex-col gap-3 text-sm text-brand-neutral-dark/80">
              <li className="flex items-start gap-2.5">
                <i className="fa-light fa-location-dot mt-1 text-brand-sage" aria-hidden="true" />
                <span>{businessInfo.address}</span>
              </li>
              <li className="flex items-center gap-2.5">
                <i className="fa-light fa-phone text-brand-sage" aria-hidden="true" />
                <a 
                  href={`tel:+45${businessInfo.phone.replace(/\s/g, '')}`} 
                  className="hover:text-brand-sage transition-colors duration-300 whitespace-nowrap"
                >
                  {businessInfo.phone}
                </a>
              </li>
              {businessInfo.email && (
                <li className="flex items-center gap-2.5">
                  <i className="fa-light fa-envelope text-brand-sage" aria-hidden="true" />
                  <a 
                    href={`mailto:${businessInfo.email}`} 
                    className="hover:text-brand-sage transition-colors duration-300 whitespace-nowrap"
                  >
                    {businessInfo.email}
                  </a>
                </li>
              )}
              <li className="flex items-center gap-2.5 pt-2 border-t border-brand-sage-medium/10">
                <span className="text-xs font-semibold uppercase tracking-wider text-brand-neutral-light">CVR:</span>
                <span className="text-xs">{businessInfo.cvr}</span>
              </li>
            </ul>
          </div>

          {/* Column 4: Image */}
          <div className="sm:col-span-2 md:col-span-1 lg:col-span-1 flex flex-col gap-4">
            <h3 className="font-serif text-base font-semibold tracking-wide">Klinikken</h3>
            <div className="w-full overflow-hidden rounded-2xl border border-brand-sage-medium/10 shadow-sm bg-white/10">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=80,format=auto/assets/d7ec2ecb-d3f6-4a99-ad2f-51a97712ea7d/475229915-1157986399449476-6645532474984307171-n.jpg"
                alt="Stausbroe Bowen Terapi klinik"
                className="w-full h-auto block object-contain"
                loading="lazy"
              />
            </div>
          </div>

        </div>

        {/* Bottom Bar */}
        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-brand-sage-medium/10 pt-8 text-xs text-brand-neutral-light md:flex-row">
          <p>© {currentYear} {businessInfo.name}. Alle rettigheder forbeholdes.</p>
          <div className="flex gap-6">
            <span className="inline-flex items-center gap-1.5">
              <i className="fa-light fa-shield-check text-brand-sage" />
              RAB Godkendt Klinik
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
